import { PAYMENT_CONTEXT_STORAGE } from '@/pages/SealedVinylPage/lib/constants'
import { isNaverpaySuccess, parseNaverpayReturnQuery, parseTossSuccessQuery } from '@/payment/sdk'
import type { PostProductPaymentProcessBody } from '@/payment/types'

export type ParseResult =
  | { type: 'process'; body: PostProductPaymentProcessBody }
  | { type: 'naverFail'; rawQuery: string }
  | { type: 'unknown' }
  | { type: 'missingContext' }

// iOS Safari Private 모드·일부 webview는 sessionStorage 접근 시 SecurityError throw.
// 미설정·접근 실패 모두 null로 정규화해서 호출자가 동일하게 처리하게 한다.
function safeGetSessionItem(key: string): string | null {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

/**
 * PG redirect 쿼리 + sessionStorage 컨텍스트 → process 중계 분기 결정.
 *  - 토스 success: process body 구성 (paymentId 유실 시 missingContext)
 *  - 네이버 success: process body (amount 도 sessionStorage 복원)
 *  - 네이버 취소/실패(resultCode≠Success 또는 비성공): naverFail
 *  - 그 외: unknown
 */
export function parseEntryContext(searchParams: URLSearchParams): ParseResult {
  const tossSuccess = parseTossSuccessQuery(searchParams)
  const naverReturn = parseNaverpayReturnQuery(searchParams)

  const paymentIdRaw = safeGetSessionItem(PAYMENT_CONTEXT_STORAGE.PAYMENT_ID)
  const paymentId = paymentIdRaw == null ? NaN : Number(paymentIdRaw)
  const hasContextPaymentId = !Number.isNaN(paymentId)

  if (tossSuccess) {
    if (!hasContextPaymentId) return { type: 'missingContext' }
    return {
      type: 'process',
      body: {
        paymentId,
        paymentMethodType: 'TOSS_QUICK_ACCOUNT',
        paymentKey: tossSuccess.paymentKey,
        amount: tossSuccess.amount,
      },
    }
  }

  // paymentId 없이 resultCode 만 오는 취소 케이스 (팝업 닫기 등) 도 naverFail 로 처리
  const naverResultCode = searchParams.get('resultCode')
  if (naverResultCode && naverResultCode !== 'Success') {
    return { type: 'naverFail', rawQuery: searchParams.toString() }
  }

  if (naverReturn) {
    if (!isNaverpaySuccess(naverReturn)) {
      return { type: 'naverFail', rawQuery: searchParams.toString() }
    }
    if (!hasContextPaymentId) return { type: 'missingContext' }
    const amountRaw = safeGetSessionItem(PAYMENT_CONTEXT_STORAGE.AMOUNT)
    const amount = amountRaw == null ? NaN : Number(amountRaw)
    if (Number.isNaN(amount)) return { type: 'missingContext' }
    return {
      type: 'process',
      body: {
        paymentId,
        paymentMethodType: 'NAVERPAY',
        paymentKey: naverReturn.paymentId,
        amount,
      },
    }
  }

  return { type: 'unknown' }
}
